import React from 'react'
import CreatePath from './CreatePath'
import PathPage, { PathPageProps } from './PathPage'
import { isConsumerMobileNative } from '#src/services/mobile'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { withIdentity, WithIdentityProps } from '#src/containers/Identity'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { handleDestinationLocation, getFirstUncompletedPage } from '#src/services/path'
import { HandlePathCreatedProps } from '.'
import { PathLayouts } from '#src/typings'
import { FlowState } from './index'
import {
  PathFlowFragment,
  PathFlowOnCompleteLeadFragment,
  CreatePathFlowInput
} from '#src/generated/graphql'
import { ifBridgeDefined } from '#src/services/dealNativeBridge'
import { UnregisterCallback } from 'history'
import uniq from 'lodash/uniq'
import { invariant } from '#src/services/errors'
import { withUserAgent, WithUserAgentProps } from '#src/containers/UserAgent'
import { getUserRequestAttributesFromMyself } from '#src/services/tracking'
import logger from '#src/services/logger'

interface PathProps {
  sourceKey: string
  pathIntent: string
  categorySlug?: string
  className?: string
  flowId?: string
  pathSlug?: string
  pageSlug?: string
  pathLayout?: PathLayouts
  prefillData?: CreatePathFlowInput
  resetScrollOnSubmit?: boolean
  onPathComplete?: () => void
  onPathCreated?: (flowState: FlowState, pathLayout?: PathLayouts) => void
}

interface PathState {
  flowState: FlowState
  previousPaths: string[]
  previousPages: string[]
  pathLayout?: PathLayouts
  pathScrollRef?: HTMLDivElement | Window
}

class Path extends React.Component<
  PathProps & WithAnalyticsProps & WithIdentityProps & RouteComponentProps<{}> & WithUserAgentProps,
  PathState
> {
  backListener?: UnregisterCallback

  constructor(
    props: PathProps &
      WithAnalyticsProps &
      WithIdentityProps &
      RouteComponentProps<{}> &
      WithUserAgentProps
  ) {
    super(props)

    this.state = {
      pathLayout: props.pathLayout,
      previousPaths: [],
      previousPages: [],
      flowState: {
        pathSlug: props.pathSlug,
        pageSlug: props.pageSlug,
        flowId: props.flowId
      }
    }

    this.handleUpdateScrollRef = this.handleUpdateScrollRef.bind(this)
    this.handlePathCompleted = this.handlePathCompleted.bind(this)
    this.handlePageForward = this.handlePageForward.bind(this)
    this.handlePageBack = this.handlePageBack.bind(this)
    this.handleMobileNativePageBack = this.handleMobileNativePageBack.bind(this)
    this.handlePageSubmitted = this.handlePageSubmitted.bind(this)
    this.handlePathCreated = this.handlePathCreated.bind(this)
  }

  public componentDidMount() {
    /**
     * Listen to browser activity to sync state between the component
     * and the browser. This also lets the browser back action actively
     * jump to a previous path if one exists
     */
    this.backListener = this.props.history.listen((_location, action) => {
      if (action === 'POP') {
        this.setState(prevState => {
          if (prevState.previousPages.length > 0) {
            /**
             * If there are any pages in the stack, move backward a page
             */
            const previousPageSlug = prevState.previousPages.pop()

            return {
              previousPaths: prevState.previousPaths || [],
              previousPages: prevState.previousPages || [],
              flowState: { ...prevState.flowState, pageSlug: previousPageSlug }
            }
          } else if (prevState.previousPages.length === 0 && prevState.previousPaths.length > 0) {
            /**
             * If there are no pages in the stack, check to see if there are any paths
             */
            const previousPathSlug = prevState.previousPaths.pop()
            if (previousPathSlug) {
              return {
                previousPaths: prevState.previousPaths || [],
                previousPages: [],
                flowState: {
                  pathSlug: previousPathSlug,
                  flowId: undefined,
                  pageSlug: undefined
                }
              }
            }
          }

          /**
           * If we mashed going backward in our browser but are somehow still on
           * the path pages, reset the state entirely. We shouldn't be here.
           */
          return { previousPaths: [], previousPages: [], flowState: {} }
        })
      }
    })
  }

  public componentWillUnmount() {
    if (this.backListener) {
      const backListenerUnlisten = this.props.history.listen(this.backListener)
      // The meothd returns from a history listen is an unlistener method
      backListenerUnlisten()
    }
  }

  private getLeadFromCompletedFlow(flow: PathFlowFragment): PathFlowOnCompleteLeadFragment {
    if (!flow.completed) {
      return invariant('Can not get a request ID for an in-progress path flow.')
    }

    // If `flow.completed` is true, `flow.onComplete` will be present.
    const onComplete = flow.onComplete!

    switch (onComplete.__typename) {
      case 'PathFlowOnCompleteLead':
        return onComplete.lead
      // @deprecated PathFlowOnCompleteLead should be used instead
      case 'PathFlowOnCompleteBroadcast':
        // It's guaranteed that conversation's created after a path will be associated with a lead
        return onComplete.conversation.lead!
      default:
        return invariant(
          `Can not get a request ID for flows ending with an completion type of ${onComplete.__typename}`
        )
    }
  }

  private trackRequestSubmitted(flow: PathFlowFragment) {
    if (this.props.analytics) {
      let lead
      try {
        lead = this.getLeadFromCompletedFlow(flow)
      } catch (e) {
        logger.captureException(e)
        return
      }

      if (lead && lead.latestRequest) {
        this.props.analytics.events.requestSubmitted({
          ...getUserRequestAttributesFromMyself(this.props.myself),
          // It is guaranteed that leads created from completing a path will have a request
          request_id: lead.latestRequest.id,
          lead_id: lead.id,
          path_flow_id: flow.id,
          path_flow_slug: flow.slug,
          category_slug: flow.category?.slug,
          parent_category_slug: flow.category?.slug,
          content_category: flow.category?.slug,
          experiment_variation: flow.slug
        })
      } else {
        logger.captureException(`Lead or latestRequest is undefined for flow ID [${flow.id}]`)
      }
    }
  }

  public handleMobileNativePageBack() {
    return ifBridgeDefined(bridge => {
      /**
       * If logged in, send them to the home, if logged out, send them back to the onboarding screen
       * HACK: We need to make this more intelligent
       *   1. User's should be recirected to the page they entered the path from, not always '/'
       *   2. If a user selects "Back" to an intersitial page they become stuck since they can't go back further (no back btn)
       */
      if (this.props.myself) {
        this.props.history.push('/')
      } else {
        bridge.sendToNative({
          type: 'navigateToNativeScreen',
          screen: {
            name: 'onboarding'
          }
        })
      }

      return null
    })
  }

  /**
   * Handle a back button press from within the path, whether it is
   * to a previous page or a previous path
   */
  public handlePageBack() {
    const previousPagesStack = this.state.previousPages.slice()
    const previousPageSlug = previousPagesStack.pop()

    if (previousPageSlug) {
      /**
       * If we're moving backward, use the browser to manage the state so we don't have to sync it ourselves.
       * If we're not manipulating the url (state-based path), then set the state manually since a browser
       * back button isn't even an option.
       */
      if (this.props.pathLayout !== PathLayouts.MODAL) {
        this.props.history.goBack()
      } else {
        this.setState({
          flowState: { ...this.state.flowState, pageSlug: previousPageSlug },
          previousPages: previousPagesStack
        })
      }

      /**
       * Track the movement backward in the path to the previous
       * page slug we were on
       */
      this.props.analytics &&
        this.props.analytics.events.pathPageViewed({
          path_flow_id: this.state.flowState.flowId || '',
          path_slug: this.state.flowState.pathSlug || '',
          page_slug: previousPageSlug || ''
        })
    } else if (this.state.previousPaths.length > 0) {
      const previousPathArr = this.state.previousPaths.slice()
      const previousPathSlug = previousPathArr.pop()

      /**
       * If there is a previous path slug, send them here and they'll create a new path
       */
      if (previousPathSlug) {
        this.setState({
          previousPaths: previousPathArr,
          flowState: {
            pathSlug: previousPathSlug,
            pageSlug: undefined,
            flowId: undefined
          }
        })
      }
    } else {
      throw new Error('User navigated backward in path with no available destination')
    }
  }

  public handlePathCompleted(flow: PathFlowFragment) {
    if (!flow.onComplete) {
      throw new Error(`Path is completed without onComplete handler, flow id [${flow.id}]`)
    }

    if (flow.onComplete.__typename !== 'PathFlowOnCompleteNewPath') {
      /**
       * If we don't redirect to a new flow and the path is completed,
       * it is safe to perform a url redirect here. The destination
       * should always come from the path schema.
       */
      const redirectOrCallback = handleDestinationLocation(flow, this.props.onPathComplete)

      if (this.props.refetchIdentity) {
        /**
         * Log the user in after the path completes
         */
        this.props.refetchIdentity().then(() => {
          /**
           * Track the request submitted event
           */
          if (
            flow.onComplete?.__typename === 'PathFlowOnCompleteBroadcast' ||
            flow.onComplete?.__typename === 'PathFlowOnCompleteLead'
          ) {
            this.trackRequestSubmitted(flow)
          }

          // Fire facebook tracking event if user completed expert-application path
          if (
            flow.onComplete &&
            flow.onComplete.__typename === 'PathFlowOnCompleteExpertApplication'
          ) {
            this.props.analytics && this.props.analytics.events.expertApplicationSubmitted()
          }

          if (typeof redirectOrCallback === 'string') {
            /**
             * In the event that an onPathComplete method is passed in
             * along with the this being a url redirect, execute it.
             */
            if (this.props.onPathComplete) {
              this.props.onPathComplete()
            }

            /**
             * Wipe the path layout so if a user drops back into the path
             * they don't have an incumbent state set
             */
            this.setState({
              pathLayout: undefined
            })

            return this.props.history.push(redirectOrCallback)
          } else if (typeof redirectOrCallback === 'function') {
            return redirectOrCallback()
          } else {
            throw new Error(`Invalid destination location returned for path, flow id [${flow.id}]`)
          }
        })
      }
    } else {
      /**
       * If the onComplete action of the flow is to start a new path, then
       * reset all the state variables just like we created a new path.
       */
      const nextFlow = flow.onComplete.flow
      const nextFlowFirstPage = getFirstUncompletedPage(nextFlow.pagesConnection)

      /**
       * Store the completed path so we can go back if we want to and reset the
       * pages to be empty since we're on a new path
       */
      this.setState(
        prevState => {
          return {
            previousPaths: uniq([
              ...(prevState.previousPaths ? prevState.previousPaths : []),
              flow.slug
            ]),
            previousPages: []
          }
        },
        () => {
          this.handlePathCreated({
            flowState: {
              flowId: nextFlow.id,
              pageSlug: nextFlowFirstPage ? nextFlowFirstPage.slug : '',
              pathSlug: nextFlow.slug
            },
            pathLayout: this.state.pathLayout,
            sourceKey: 'branching-path',
            pathIntent: 'branching-path',
            previousPathFlowId: flow.id
          })
        }
      )
    }
  }

  /**
   * A new path was created, either invoke the props or update the state
   */
  public handlePathCreated({
    flowState: { pathSlug, pageSlug, flowId },
    pathLayout,
    sourceKey,
    pathIntent,
    previousPathFlowId
  }: HandlePathCreatedProps) {
    if (this.props.analytics && flowId && pathSlug) {
      const derivedSourceKey = this.props.sourceKey || sourceKey || ''
      const deriedPathIntent = this.props.pathIntent || pathIntent || ''

      this.props.analytics.events.pathFlowCreated({
        source_key: derivedSourceKey,
        path_intent: deriedPathIntent as Tracking.PathIntent,
        path_flow_id: flowId,
        path_slug: pathSlug,
        previous_path_flow_id: previousPathFlowId,
        experiment_variation: pathSlug
      })

      this.props.analytics.events.pathPageViewed({
        path_flow_id: flowId,
        path_slug: pathSlug,
        page_slug: pageSlug || ''
      })
    }

    this.setState(prevState => {
      let newPathLayout

      /**
       * Favor the pathLayout in state since we could be coming from another path
       * and don't want the styles to change midway through a continuous flow.
       */
      if (prevState.pathLayout !== undefined && prevState.pathLayout !== pathLayout) {
        newPathLayout = prevState.pathLayout
      } else {
        newPathLayout = pathLayout
      }

      return {
        pathLayout: newPathLayout,
        flowState: {
          pathSlug,
          pageSlug,
          flowId
        }
      }
    })

    if (this.props.onPathCreated) {
      this.props.onPathCreated(
        {
          pathSlug,
          pageSlug,
          flowId
        },
        pathLayout
      )
    }
  }

  /**
   * The path navigated forward, update the state
   */
  public handlePageForward({ pathSlug, pageSlug, flowId }: FlowState) {
    this.setState(
      prevState => {
        const flowState = {
          flowState: {
            flowId,
            pathSlug,
            pageSlug
          }
        }

        if (prevState.flowState.pageSlug) {
          return {
            ...prevState,
            ...flowState,
            previousPages: [
              ...(prevState.previousPages ? prevState.previousPages : []),
              prevState.flowState.pageSlug
            ]
          }
        }

        return {
          ...prevState,
          ...flowState
        }
      },
      () => {
        this.props.analytics &&
          this.props.analytics.events.pathPageViewed({
            path_flow_id: flowId || '',
            path_slug: pathSlug || '',
            page_slug: pageSlug || ''
          })
      }
    )

    /**
     * Only update the url state if we're not in a modal
     */
    if (this.props.pathLayout !== PathLayouts.MODAL) {
      this.props.history.push(
        `/requests/${pathSlug}${pageSlug ? `/${pageSlug}` : ''}?flowId=${flowId}`
      )
    }
  }

  public handlePageSubmitted(pathFlow: PathFlowFragment, isNewUserRegistered: boolean) {
    /**
     * This allows layouts to determine what specific element should reset it's scroll.
     * We can't blanket assume it's `window` for everything.
     */
    if (this.state.pathScrollRef && this.props.resetScrollOnSubmit) {
      this.state.pathScrollRef.scrollTo(0, 0)
    }

    /**
     * We track this here because the `track` method has some context on the user
     * that the API does not. A new user can be reigstered at any point.
     */
    let refetchIdentity: Promise<null> = Promise.resolve(null)
    if (isNewUserRegistered && this.props.refetchIdentity) {
      refetchIdentity = this.props.refetchIdentity().then(result => {
        const myself = result.data.me

        if (myself && this.props.analytics) {
          this.props.analytics.events.userRegistered({
            user_id: myself.id,
            context: 'path-complete'
          })
        }

        return null
      })
    }

    // We have to wait for the (potential) refetch to finish before proceeding.
    //  Some side-effect of completing the path causes the `refetchIdentity`
    //  promise to resolve with stale data. This shouldn't be necessary...
    refetchIdentity.then(() => {
      if (pathFlow.completed && pathFlow.onComplete) {
        /**
         * Is the path flow completed?
         */
        this.handlePathCompleted(pathFlow)
      } else if (pathFlow.pagesConnection.edges.length > 0) {
        /**
         * We're still in the path, redirect to the next node
         */
        const lastEdge = pathFlow.pagesConnection.edges.slice(-1)

        this.handlePageForward({
          flowId: pathFlow.id,
          pathSlug: pathFlow.slug,
          pageSlug: lastEdge[0].node.slug
        })
      } else {
        /**
         * This path is not completed and the edges are empty, this is a fatal error
         */
        throw new Error(`Path is not completed and edges are empty, flow id [${pathFlow.id}]`)
      }
    })
  }

  public handleUpdateScrollRef(scrollRef: HTMLDivElement | Window) {
    this.setState({
      pathScrollRef: scrollRef
    })
  }

  public render() {
    const { pathSlug, pageSlug, flowId } = this.state.flowState

    /**
     * They're rendering a new page or continuing a flow as normal
     */
    if (flowId && pathSlug) {
      const pathProps: PathPageProps = {
        className: this.props.className,
        pageSlug: pageSlug,
        flowId: flowId,
        onUpdateScrollRef: this.handleUpdateScrollRef,
        onPageBack: this.handlePageBack,
        onMobileNativePageBack: this.handleMobileNativePageBack,
        onPageSubmitted: this.handlePageSubmitted,
        previousPageSlugs: this.state.previousPages,
        previousPathSlugs: this.state.previousPaths
      }

      let pathLayout
      if (isConsumerMobileNative(this.props.userAgent)) {
        /**
         * Consumer Native always uses the default layout of the path system.
         */
        pathLayout = PathLayouts.DEFAULT
      } else if (this.props.pathLayout !== undefined) {
        /**
         * If there is a path layout passed in the props, it takes precedence.
         */
        pathLayout = this.props.pathLayout
      } else {
        /**
         * If nothing is passed in via the props, fall back to what is in state. An empty
         * state object will be handled on render by falling back to the default.
         */
        pathLayout = this.state.pathLayout
      }

      return <PathPage pathLayout={pathLayout} {...pathProps} />
    }

    if (!this.props.categorySlug && !this.props.pathSlug && !this.state.flowState.pathSlug) {
      /**
       * The parameters needs to be optional to instantiate a path different
       * ways, because of this you could start a path with insufficient data.
       */
      throw new Error('Insufficient parameters passed to start a path')
    }

    return (
      <CreatePath
        categorySlug={this.props.categorySlug}
        pathSlug={this.state.flowState.pathSlug || this.props.pathSlug}
        onPathCreated={this.handlePathCreated}
        onPathCompleted={this.handlePathCompleted}
        prefillData={this.props.prefillData}
        pathLayout={this.state.pathLayout}
      />
    )
  }
}

export default withAnalytics(withIdentity(withRouter(withUserAgent(Path))))
